import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, Lock, User, Phone, AlertCircle, CreditCard, ArrowRight, Building2, UserCircle, Upload, X, FileText } from 'lucide-react';
import { Logo } from '../components/Logo';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// الحد الأقصى لحجم الملف (5 ميجابايت)
const MAX_FILE_SIZE = 5 * 1024 * 1024;

export default function Register() {
  const navigate = useNavigate();
  const { t, isRTL, dir } = useLanguage();
  const [accountType, setAccountType] = useState(''); // 'individual' أو 'company'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    national_id: '',
    role: 'client',
    account_type: '',
    // حقول المنشآت
    company_name: '',
    manager_phone: '',
  });
  
  // ملفات المنشآت
  const [commercialRegister, setCommercialRegister] = useState(null);
  const [managerIdImage, setManagerIdImage] = useState(null);
  const commercialRegisterRef = useRef(null);
  const managerIdRef = useRef(null);
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateNationalId = (id) => {
    return /^\d{10}$/.test(id);
  };

  const handleFileUpload = (e, setFile) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > MAX_FILE_SIZE) {
      setError(t('msg.fileTooLarge'));
      return;
    }
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setError(t('msg.unsupportedFormat'));
      return;
    }
    
    setFile(file);
    setError('');
    e.target.value = '';
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve) => {
      if (!file) {
        resolve(null);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        resolve({
          name: file.name,
          type: file.type,
          size: file.size,
          data: reader.result.split(',')[1]
        });
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    // التحقق من رقم الهوية
    if (!validateNationalId(formData.national_id)) {
      if (accountType === 'individual') {
        setError('رقم الهوية يجب أن يتكون من 10 أرقام');
      } else {
        setError('الرقم الوطني للمنشأة يجب أن يتكون من 10 أرقام');
      }
      return;
    }

    // التحقق من ملفات المنشآت
    if (accountType === 'company') {
      if (!commercialRegister) {
        setError('يرجى إرفاق صورة السجل التجاري');
        return;
      }
      if (!managerIdImage) {
        setError('يرجى إرفاق صورة هوية المسؤول');
        return;
      }
      if (!formData.company_name.trim()) {
        setError('يرجى إدخال اسم المنشأة');
        return;
      }
      if (!formData.manager_phone || formData.manager_phone.length !== 10) {
        setError('يرجى إدخال رقم جوال المسؤول بشكل صحيح (10 أرقام)');
        return;
      }
    }

    setLoading(true);

    try {
      // تحويل الملفات لـ Base64
      const commercialRegisterData = await fileToBase64(commercialRegister);
      const managerIdData = await fileToBase64(managerIdImage);

      const payload = {
        ...formData,
        account_type: accountType,
        role: 'client',
        // إضافة الملفات للمنشآت
        ...(accountType === 'company' && {
          commercial_register: commercialRegisterData,
          manager_id_image: managerIdData,
        })
      };

      const response = await axios.post(`${API}/auth/register`, payload);
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'فشل إنشاء الحساب');
    } finally {
      setLoading(false);
    }
  };

  // عرض اختيار نوع الحساب
  if (!accountType) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center py-12" dir={dir}>
        {/* Language Selector */}
        <div className="fixed top-4 left-4 z-50">
          <LanguageSelector />
        </div>
        
        <div className="w-full max-w-lg px-6">
          <div className="bg-white rounded-xl shadow-xl p-8">
            {/* زر العودة */}
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-500 hover:text-primary mb-6 font-body"
            >
              <ArrowRight className="w-5 h-5" />
              {t('nav.home')}
            </button>

            <div className="flex items-center justify-center gap-3 mb-8">
              <Logo size="lg" />
              <h1 className="text-2xl font-heading font-bold text-primary">
                {t('register.title')}
              </h1>
            </div>

            <p className="text-center text-gray-600 mb-8 font-body">
              {t('register.selectAccountType')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* حساب أفراد */}
              <button
                onClick={() => setAccountType('individual')}
                data-testid="account-type-individual"
                className="p-6 border-2 border-gray-200 rounded-xl hover:border-gold hover:bg-gold/5 transition-all group"
              >
                <div className="flex flex-col items-center gap-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <UserCircle className="w-8 h-8 text-blue-600" />
                  </div>
                  <div className="text-center">
                    <h3 className="font-heading font-bold text-primary text-lg mb-2">{t('register.individual')}</h3>
                    <p className="text-sm text-gray-500 font-body">{t('register.individualDesc')}</p>
                  </div>
                </div>
              </button>

              {/* حساب منشآت */}
              <button
                onClick={() => setAccountType('company')}
                data-testid="account-type-company"
                className="p-6 border-2 border-gray-200 rounded-xl hover:border-gold hover:bg-gold/5 transition-all group"
              >
                <div className="flex flex-col items-center gap-4">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                    <Building2 className="w-8 h-8 text-purple-600" />
                  </div>
                  <div className="text-center">
                    <h3 className="font-heading font-bold text-primary text-lg mb-2">{t('register.company')}</h3>
                    <p className="text-sm text-gray-500 font-body">{t('register.companyDesc')}</p>
                  </div>
                </div>
              </button>
            </div>

            <div className="mt-6 text-center">
              <p className="text-gray-600 font-body">
                {t('register.haveAccount')}{' '}
                <button
                  onClick={() => navigate('/login')}
                  className="text-gold hover:underline font-semibold"
                >
                  {t('nav.login')}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand flex items-center justify-center py-12" dir={dir}>
      {/* Language Selector */}
      <div className="fixed top-4 left-4 z-50">
        <LanguageSelector />
      </div>
      
      <div className="w-full max-w-md px-6">
        <div className="bg-white rounded-xl shadow-xl p-8">
          {/* زر العودة */}
          <button
            onClick={() => setAccountType('')}
            className="flex items-center gap-2 text-gray-500 hover:text-primary mb-6 font-body"
          >
            <ArrowRight className="w-5 h-5" />
            {t('register.changeAccountType')}
          </button>

          <div className="flex items-center justify-center gap-3 mb-6">
            <Logo size="lg" />
            <div className="text-center">
              <h1 className="text-2xl font-heading font-bold text-primary">
                {accountType === 'individual' ? 'تسجيل حساب أفراد' : 'تسجيل حساب منشآت'}
              </h1>
              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs mt-2 ${
                accountType === 'individual' 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-purple-100 text-purple-700'
              }`}>
                {accountType === 'individual' ? (
                  <><UserCircle className="w-3 h-3" /> أفراد</>
                ) : (
                  <><Building2 className="w-3 h-3" /> منشآت</>
                )}
              </span>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-center gap-3" data-testid="register-error">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700 font-body">{error}</p>
            </div>
          )}

          <form onSubmit={handleRegister} className="space-y-4">
            {/* ======== حقول المنشآت الإضافية ======== */}
            {accountType === 'company' && (
              <>
                {/* اسم المنشأة */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                    اسم المنشأة <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Building2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      data-testid="register-company-name-input"
                      type="text"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      required
                      className="w-full pr-10 pl-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                      placeholder="أدخل اسم المنشأة"
                    />
                  </div>
                </div>
              </>
            )}

            {/* الاسم الكامل / اسم المسؤول */}
            <div>
              <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                {accountType === 'individual' ? 'الاسم الكامل' : 'اسم المسؤول'} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <User className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="register-name-input"
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  className="w-full pr-10 pl-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                  placeholder={accountType === 'individual' ? 'أدخل اسمك الكامل' : 'أدخل اسم المسؤول'}
                />
              </div>
            </div>

            {/* رقم الهوية / الرقم الوطني للمنشأة */}
            <div>
              <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                {accountType === 'individual' ? 'رقم الهوية' : 'الرقم الوطني للمنشأة'} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <CreditCard className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="register-national-id-input"
                  type="text"
                  name="national_id"
                  value={formData.national_id}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setFormData({ ...formData, national_id: val });
                  }}
                  required
                  maxLength={10}
                  className="w-full pr-10 pl-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                  placeholder={accountType === 'individual' ? 'أدخل رقم الهوية (10 أرقام)' : 'أدخل الرقم الوطني للمنشأة (10 أرقام)'}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1 font-body">
                {formData.national_id.length}/10 أرقام
                {formData.national_id.length === 10 && <span className="text-green-600 mr-2">✓</span>}
                {accountType === 'individual' && ' - سيتم استخدام رقم الهوية كرقم تعريفي لك'}
              </p>
            </div>

            {/* ======== حقول المنشآت: رقم جوال المسؤول وصور ======== */}
            {accountType === 'company' && (
              <>
                {/* رقم جوال المسؤول */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                    رقم جوال المسؤول <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      data-testid="register-manager-phone-input"
                      type="tel"
                      name="manager_phone"
                      value={formData.manager_phone}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setFormData({ ...formData, manager_phone: val });
                      }}
                      required
                      maxLength={10}
                      className="w-full pr-10 pl-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                      placeholder="05xxxxxxxx"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1 font-body">
                    {formData.manager_phone.length}/10 أرقام
                    {formData.manager_phone.length === 10 && <span className="text-green-600 mr-2">✓</span>}
                  </p>
                </div>

                {/* صورة السجل التجاري */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                    صورة السجل التجاري <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    ref={commercialRegisterRef}
                    onChange={(e) => handleFileUpload(e, setCommercialRegister)}
                    accept=".pdf,.jpg,.jpeg,.png,.gif"
                    className="hidden"
                  />
                  {commercialRegister ? (
                    <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-green-600" />
                        <span className="text-sm font-body text-green-700 truncate max-w-[200px]">
                          {commercialRegister.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setCommercialRegister(null)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => commercialRegisterRef.current?.click()}
                      data-testid="upload-commercial-register-btn"
                      className="w-full p-4 border-2 border-dashed border-gray-300 rounded-md hover:border-gold hover:bg-gold/5 transition-all font-body flex items-center justify-center gap-2 text-gray-600"
                    >
                      <Upload className="w-5 h-5" />
                      <span>اضغط لرفع صورة السجل التجاري</span>
                    </button>
                  )}
                  <p className="text-xs text-gray-500 mt-1 font-body">الصيغ المدعومة: PDF, JPG, PNG (حد أقصى 5 ميجابايت)</p>
                </div>

                {/* صورة هوية المسؤول */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                    صورة هوية المسؤول <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    ref={managerIdRef}
                    onChange={(e) => handleFileUpload(e, setManagerIdImage)}
                    accept=".pdf,.jpg,.jpeg,.png,.gif"
                    className="hidden"
                  />
                  {managerIdImage ? (
                    <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-green-600" />
                        <span className="text-sm font-body text-green-700 truncate max-w-[200px]">
                          {managerIdImage.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setManagerIdImage(null)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => managerIdRef.current?.click()}
                      data-testid="upload-manager-id-btn"
                      className="w-full p-4 border-2 border-dashed border-gray-300 rounded-md hover:border-gold hover:bg-gold/5 transition-all font-body flex items-center justify-center gap-2 text-gray-600"
                    >
                      <Upload className="w-5 h-5" />
                      <span>اضغط لرفع صورة هوية المسؤول</span>
                    </button>
                  )}
                  <p className="text-xs text-gray-500 mt-1 font-body">الصيغ المدعومة: PDF, JPG, PNG (حد أقصى 5 ميجابايت)</p>
                </div>
              </>
            )}

            {/* البريد الإلكتروني */}
            <div>
              <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                البريد الإلكتروني <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Mail className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="register-email-input"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full pr-10 pl-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                  placeholder="example@email.com"
                />
              </div>
            </div>

            {/* رقم الهاتف (للأفراد فقط - المنشآت لديها رقم جوال المسؤول) */}
            {accountType === 'individual' && (
              <div>
                <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                  رقم الجوال <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Phone className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    data-testid="register-phone-input"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setFormData({ ...formData, phone: val });
                    }}
                    required
                    maxLength={10}
                    className="w-full pr-10 pl-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                    placeholder="05xxxxxxxx"
                  />
                </div>
              </div>
            )}

            {/* كلمة المرور */}
            <div>
              <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                كلمة المرور <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Lock className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="register-password-input"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  className="w-full pr-10 pl-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                  placeholder="••••••••"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1 font-body">
                كلمة المرور يجب أن تكون 6 أحرف على الأقل
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit-button"
              className={`w-full py-3 text-white font-semibold rounded-md transition-all duration-300 disabled:opacity-50 font-body ${
                accountType === 'individual' 
                  ? 'bg-gold hover:bg-gold-dark' 
                  : 'bg-purple-500 hover:bg-purple-600'
              }`}
            >
              {loading ? 'جاري التسجيل...' : 'تسجيل حساب جديد'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600 font-body">
              لديك حساب بالفعل؟{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-gold hover:underline font-semibold"
              >
                تسجيل الدخول
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
